import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FileScanFileDto, FileScanService } from '../file-scan.service';
import { DatePipe } from '@angular/common';
import { SortConfig } from '../file-scan.component';

@Component({
  selector: 'app-file-scan-item',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './file-scan-item.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./file-scan-item.component.scss']
})
export class FileScanItemComponent implements OnChanges {
  @Input() scanUid!: string;
  @Input() item!: FileScanFileDto;
  @Input() depth = 0;
  @Input() sortConfig!: SortConfig;
  @Input() parentSize: number | null = null;

  children: FileScanFileDto[] = [];
  expanded = false;
  loading = false;
  loaded = false;

  constructor(private fileScanService: FileScanService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sortConfig'] && !changes['sortConfig'].firstChange && this.children.length > 0) {
      this.children = this.sortFiles(this.children);
    }
  }

  toggle(): void {
    if (this.item.type !== 'DIRECTORY') {
      return;
    }

    this.expanded = !this.expanded;

    if (this.expanded && !this.loaded) {
      this.loadChildren();
    }
  }

  loadChildren(): void {
    this.loading = true;
    this.fileScanService.getScanFiles(this.scanUid, this.item.id).subscribe({
      next: (files) => {
        this.children = this.sortFiles(files);
        this.loaded = true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  sortFiles(files: FileScanFileDto[]): FileScanFileDto[] {
    return [...files].sort((a, b) => {
      // Directories always come first
      if (a.type === 'DIRECTORY' && b.type !== 'DIRECTORY') {
        return -1;
      }
      if (a.type !== 'DIRECTORY' && b.type === 'DIRECTORY') {
        return 1;
      }

      const column = this.sortConfig.column;
      const direction = this.sortConfig.direction === 'asc' ? 1 : -1;

      let comparison = 0;
      if (column === 'path') {
        comparison = a.path.localeCompare(b.path);
      } else if (column === 'size') {
        comparison = (a.size || 0) - (b.size || 0);
      } else if (column === 'modifiedAt') {
        const dateA = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
        const dateB = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
        comparison = dateA - dateB;
      }

      return comparison * direction;
    });
  }

  formatSize(size: number | null): string {
    if (size === null) {
      return '';
    }
    if (size === 0) {
      return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return `${parseFloat((size / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  getRelativePercentage(): string {
    if (this.parentSize === null || this.parentSize === 0 || this.item.size === null) {
      return '';
    }
    const percentage = (this.item.size / this.parentSize) * 100;
    return `${percentage.toFixed(1)}%`;
  }
}
